import React, { useEffect, useMemo, useState } from 'react';
import { Trash2, X } from 'lucide-react';
import { useInventoryStore, StockLog } from '../../store/inventoryStore';
import { useWarehouseStore } from '../../store/warehouseStore';
import { useProjectStore } from '../../store/projectStore';

interface Props {
  unitId: string;
  onClose: () => void;
}

const fmtDate = (d: Date) => {
  const date = d instanceof Date ? d : new Date(d);
  if (isNaN(date.getTime())) return '—';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${day}/${month}/${year} ${hours}:${minutes}`;
};

const EVENT_LABEL: Record<string, string> = {
  Added:        'Added',
  Edited:       'Updated',
  add_stock:    'Added',
  return_stock: 'Returned',
  transfer:     'Transferred',
  check_out:    'Checked Out',
  Retired:       'Retired',
};

const actionBadgeStyle = (eventType: string, movementType: string | null, isDark: boolean) => {
  const key = movementType ?? eventType;
  switch (key) {
    case 'check_out':
      return { backgroundColor: isDark ? 'rgba(217,119,6,0.2)' : '#fef3c7', color: isDark ? '#fbbf24' : '#b45309' };
    case 'transfer':
      return { backgroundColor: isDark ? 'rgba(59,130,246,0.2)' : '#dbeafe', color: isDark ? '#60a5fa' : '#1d4ed8' };
    case 'return_stock':
      return { backgroundColor: isDark ? 'rgba(20,184,166,0.2)' : '#ccfbf1', color: isDark ? '#2dd4bf' : '#0f766e' };
    case 'Edited':
      return { backgroundColor: isDark ? 'rgba(107,114,128,0.25)' : '#f3f4f6', color: isDark ? '#9ca3af' : '#4b5563' };
    case 'Retired':
      return { backgroundColor: isDark ? 'rgba(239,68,68,0.2)' : '#fee2e2', color: isDark ? '#f87171' : '#b91c1c' };
    case 'Added':
    case 'add_stock':
    default:
      return { backgroundColor: isDark ? 'rgba(22,163,74,0.2)' : '#dcfce7', color: isDark ? '#4ade80' : '#15803d' };
  }
};

const statusStyle = (status: string, isDark: boolean) => {
  switch (status) {
    case 'in stock':    return isDark ? 'bg-green-900/40 text-green-400'  : 'bg-green-100 text-green-700';
    case 'checked out': return isDark ? 'bg-amber-900/40 text-amber-400'  : 'bg-amber-100 text-amber-700';
    case 'retired':     return isDark ? 'bg-red-900/40   text-red-400'    : 'bg-red-100   text-red-700';
    default:            return isDark ? 'bg-gray-700     text-gray-300'   : 'bg-gray-100  text-gray-600';
  }
};

const conditionStyle = (condition: string | null, isDark: boolean) => {
  if (condition === 'new')  return isDark ? 'bg-blue-900/40 text-blue-400' : 'bg-blue-100 text-blue-700';
  if (condition === 'used') return isDark ? 'bg-violet-900/45 text-violet-300' : 'bg-violet-100 text-violet-700';
  return isDark ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-400';
};

const StockUnitDrawer: React.FC<Props> = ({ unitId, onClose }) => {
  const { stockUnits, items, updateStockUnit } = useInventoryStore();
  const { warehouses, storageAreasByWarehouse } = useWarehouseStore();
  const { appTheme } = useProjectStore();
  const isDark = appTheme === 'dark';

  const [visible, setVisible] = useState(false);
  const [logs, setLogs] = useState<StockLog[]>([]);
  const [loadingLogs, setLoadingLogs] = useState(true);
  const [confirmRetire, setConfirmRetire] = useState(false);
  const [retiring, setRetiring] = useState(false);

  useEffect(() => { requestAnimationFrame(() => setVisible(true)); }, []);

  // Call getState() inside the effect to avoid capturing a stale function reference.
  // Retry once after 1 s if the first attempt returns empty — handles idle-connection silent failures.
  useEffect(() => {
    let cancelled = false;

    const load = async (attempt: number) => {
      if (cancelled) return;
      setLoadingLogs(true);
      try {
        const data = await useInventoryStore.getState().fetchLogs(unitId);
        if (cancelled) return;
        if (data.length === 0 && attempt === 1) {
          // First attempt returned nothing — Supabase may have silently dropped the idle connection.
          // Wait a moment and try once more with a fresh request.
          setTimeout(() => load(2), 1000);
          return;
        }
        setLogs(data);
        setLoadingLogs(false);
      } catch {
        if (!cancelled) setLoadingLogs(false);
      }
    };

    load(1);
    return () => { cancelled = true; };
  }, [unitId]);

  useEffect(() => {
    setConfirmRetire(false);
  }, [unitId]);

  const reloadLogs = async () => {
    const data = await useInventoryStore.getState().fetchLogs(unitId);
    setLogs(data);
  };

  const handleRetire = async () => {
    if (!unit || retiring || unit.status === 'retired') return;
    setRetiring(true);
    try {
      await updateStockUnit(unit.id, { status: 'retired' }, 'Unit retired');
      setConfirmRetire(false);
      await reloadLogs();
    } finally {
      setRetiring(false);
    }
  };

  const close = () => { setVisible(false); setTimeout(onClose, 220); };

  const unit = useMemo(() => stockUnits.find(u => u.id === unitId), [stockUnits, unitId]);
  const item = useMemo(() => items.find(i => i.id === unit?.itemId), [items, unit]);
  const whById = useMemo(() => new Map(warehouses.map(w => [w.id, w])), [warehouses]);

  const locationLabel = (warehouseId: string | null, areaId: string | null) => {
    if (!warehouseId) return null;
    const wh = whById.get(warehouseId);
    if (!areaId) return wh?.name ?? 'Unknown warehouse';
    const areas = storageAreasByWarehouse[warehouseId] ?? [];
    const area = areas.find(a => a.id === areaId);
    return `${wh?.name ?? 'Unknown'}${area ? ` / ${area.name}` : ''}`;
  };

  const currentWarehouse = unit ? whById.get(unit.warehouseId) : null;
  const currentArea = unit?.storageAreaId
    ? (storageAreasByWarehouse[unit.warehouseId] ?? []).find(a => a.id === unit.storageAreaId)
    : null;

  const drawerBg = isDark ? '#20242b' : '#ffffff';
  const border   = isDark ? '#343a43' : '#e5e7eb';
  const text     = isDark ? '#e5e7eb' : '#111827';
  const muted    = isDark ? '#9ca3af' : '#6b7280';
  const divider  = isDark ? '#2d3340' : '#f3f4f6';

  const movementDetail = (log: StockLog) => {
    const from = locationLabel(log.fromWarehouseId, log.fromStorageAreaId);
    const to   = locationLabel(log.toWarehouseId, log.toStorageAreaId);
    if (from && to) return `${from} → ${to}`;
    if (to) return `→ ${to}`;
    if (from) return `from ${from}`;
    return null;
  };

  const Row: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
    <div className="flex items-center justify-between gap-4 py-2" style={{ borderBottom: `1px solid ${divider}` }}>
      <span className="text-sm font-medium flex-shrink-0" style={{ color: text, minWidth: 110 }}>{label}</span>
      <span className="text-sm text-right" style={{ color: muted }}>{children}</span>
    </div>
  );

  return (
    <>
      <div
        className="fixed inset-0 z-40 transition-opacity duration-200"
        style={{ backgroundColor: 'rgba(0,0,0,0.4)', opacity: visible ? 1 : 0 }}
        onClick={close}
      />

      <div
        className="fixed top-0 right-0 h-full z-50 flex flex-col shadow-2xl"
        style={{
          width: 400,
          backgroundColor: drawerBg,
          borderLeft: `1px solid ${border}`,
          transform: visible ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 220ms cubic-bezier(0.4,0,0.2,1)',
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 flex-shrink-0" style={{ borderBottom: `1px solid ${border}` }}>
          <div>
            <p className="text-[11px] uppercase tracking-wide" style={{ color: muted }}>Stock Unit</p>
            <div className="flex items-center gap-2 mt-1">
              <h2 className="text-base font-semibold" style={{ color: text }}>
                {item?.name ?? 'Unknown item'}
              </h2>
              {unit && (
                <>
                  <span
                    className="inline-flex items-center justify-center text-sm font-medium rounded px-2 py-0.5"
                    style={{ backgroundColor: isDark ? '#374151' : '#e5e7eb', color: isDark ? '#d1d5db' : '#374151' }}
                  >
                    #{unit.unitId}
                  </span>
                  {unit.status !== 'retired' && (
                    confirmRetire ? (
                      <div className="flex items-center gap-1">
                        <span className="text-[10px] whitespace-nowrap" style={{ color: isDark ? '#fca5a5' : '#dc2626' }}>
                          Retire?
                        </span>
                        <button
                          onClick={handleRetire}
                          disabled={retiring}
                          className="px-1.5 py-0.5 rounded text-[10px] font-medium transition-colors disabled:opacity-50"
                          style={{ backgroundColor: isDark ? '#7f1d1d' : '#dc2626', color: '#fff' }}
                        >
                          {retiring ? '…' : 'Yes'}
                        </button>
                        <button
                          onClick={() => setConfirmRetire(false)}
                          disabled={retiring}
                          className="px-1.5 py-0.5 rounded text-[10px] transition-colors"
                          style={{ color: muted }}
                        >
                          No
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setConfirmRetire(true)}
                        className="p-1 rounded transition-colors hover:bg-red-500/10"
                        title="Retire unit"
                        aria-label="Retire unit"
                      >
                        <Trash2 size={14} className="text-red-400 hover:text-red-300" />
                      </button>
                    )
                  )}
                </>
              )}
            </div>
          </div>
          <button onClick={close} className="p-1.5 rounded hover:bg-gray-700/50 transition-colors" style={{ color: muted }}>
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {!unit ? (
            <div className="px-5 py-10 text-center text-sm" style={{ color: muted }}>
              Unit not found.
            </div>
          ) : (
            <>
              {/* Details */}
              <div className="px-5 pt-4 pb-2">
                <p className="text-[11px] uppercase tracking-wide font-medium mb-1" style={{ color: muted }}>
                  Details
                </p>
                <Row label="Serial / MAC">{unit.serialMac || <span style={{ color: muted }}>—</span>}</Row>
                <Row label="SKU">{item?.sku || <span style={{ color: muted }}>—</span>}</Row>
                <Row label="Category">{item?.category || <span style={{ color: muted }}>—</span>}</Row>
                <Row label="Warehouse">{currentWarehouse?.name ?? <span style={{ color: muted }}>—</span>}</Row>
                <Row label="Storage Area">{currentArea?.name ?? <span style={{ color: muted }}>—</span>}</Row>
                <Row label="Condition">
                  <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium capitalize ${conditionStyle(unit.condition, isDark)}`}>
                    {unit.condition ?? '—'}
                  </span>
                </Row>
                <Row label="Status">
                  <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium capitalize ${statusStyle(unit.status, isDark)}`}>
                    {unit.status}
                  </span>
                </Row>
                <Row label="Reference">{unit.reference || <span style={{ color: muted }}>—</span>}</Row>
                <Row label="Owner">{unit.owner || <span style={{ color: muted }}>—</span>}</Row>
                <Row label="Last Updated">{fmtDate(unit.updatedAt)}</Row>
                <Row label="Created">{fmtDate(unit.createdAt)}</Row>
                {unit.notes && <Row label="Notes">{unit.notes}</Row>}
              </div>

              {/* Divider */}
              <div className="mx-5 my-3" style={{ height: 1, backgroundColor: border }} />

              {/* History */}
              <div className="px-5 pb-6">
                <p className="text-[11px] uppercase tracking-wide font-medium mb-3" style={{ color: muted }}>
                  Event History
                </p>

                {loadingLogs && (
                  <p className="text-sm text-center py-6" style={{ color: muted }}>Loading…</p>
                )}

                {!loadingLogs && logs.length === 0 && (
                  <p className="text-sm text-center py-6" style={{ color: muted }}>No events recorded yet.</p>
                )}

                {!loadingLogs && logs.length > 0 && (
                  <div className="relative">
                    {/* Vertical timeline line */}
                    <div
                      className="absolute left-[6px] top-3 bottom-3 w-px"
                      style={{ backgroundColor: border }}
                    />

                    <div className="flex flex-col">
                      {logs.map((log, idx) => {
                        const label = EVENT_LABEL[log.movementType ?? log.eventType] ?? log.eventType;
                        const badgeStyle = actionBadgeStyle(log.eventType, log.movementType, isDark);
                        const location = movementDetail(log);

                        return (
                          <div key={log.id} className="relative pl-5 pb-5">
                            {/* Dot */}
                            <div
                              className="absolute left-0 top-[5px] w-3 h-3 rounded-full border-2"
                              style={{
                                backgroundColor: drawerBg,
                                borderColor: idx === 0 ? (isDark ? '#60a5fa' : '#3b82f6') : (isDark ? '#4b5563' : '#d1d5db'),
                              }}
                            />

                            {/* Event header: badge + date */}
                            <div className="flex items-center justify-between gap-2">
                              <span
                                className="inline-flex px-2 py-0.5 rounded-full text-sm font-medium"
                                style={badgeStyle}
                              >
                                {label}
                              </span>
                              <span className="text-sm whitespace-nowrap" style={{ color: muted }}>
                                {fmtDate(log.createdAt)}
                              </span>
                            </div>

                            {/* Description */}
                            {log.description && (
                              <p className="text-sm mt-1 leading-snug" style={{ color: text }}>
                                {log.description}
                              </p>
                            )}

                            {/* Location movement */}
                            {location && (
                              <p className="text-sm mt-0.5" style={{ color: muted }}>{location}</p>
                            )}

                            {/* Actor + reference */}
                            {(log.actedBy || log.reference) && (
                              <div className="flex items-center gap-2 mt-0.5 text-sm" style={{ color: muted }}>
                                {log.actedBy && <span>by {log.actedBy}</span>}
                                {log.actedBy && log.reference && <span style={{ color: isDark ? '#4b5563' : '#d1d5db' }}>·</span>}
                                {log.reference && <span>ref: {log.reference}</span>}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default StockUnitDrawer;
